/**
 * Tenant Usage — React Query hook for usage metrics and cost estimates
 *
 * Calls control_plane.api.tenant_usage.get_tenant_usage via raw fetch()
 * (NOT frappeMethod — that routes to Tenant Server, not Control Plane).
 * Returns user count, storage, storefronts, apps, and estimated monthly cost.
 *
 * Pattern: Matches useChatwootPulse.ts — raw fetch with graceful EMPTY_RESPONSE.
 */

import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { getCPToken, CONTROL_PLANE_URL } from "@/lib/api/controlPlaneClient";
import { useAuthStore } from "@/lib/store/authStore";

// ── Types matching backend response shape ──────────────────

export interface TenantUsageApp {
  name: string;
  display_name: string;
  status: "Active" | "Deactivated";
  activated_on: string | null;
  monthly_price_thb: number;
}

export interface TenantUsagePlan {
  price_per_user_thb: number;
  storage_included_gb: number;
  storage_overage_per_gb_thb: number;
  storefronts_included: number;
  storefront_extra_per_month_thb: number;
  // Resource limits (0 = unlimited) — added by Epic 02
  items_limit?: number;
  orders_per_month_limit?: number;
  marketplace_connections_limit?: number;
  customers_limit?: number;
  ai_queries_per_day_limit?: number;
  builder_pages_limit?: number;
  employees_limit?: number;
}

export interface TenantUsageResource {
  key: string;
  label: string;
  count: number | null; // null = not yet counted, 0 = counted but zero
  limit: number; // 0 = unlimited
  period: string | null; // null = lifetime, "month" = per month, "day" = per day
}

export interface TenantUsageResponse {
  plan_name?: string | null;
  plan_status?: string | null;
  user_count: number;
  user_cost_thb: number;
  storage_bytes: number;
  storage_gb: number;
  storage_included_gb: number;
  storage_overage_gb: number;
  storage_overage_cost_thb: number;
  storage_metered_at: string | null;
  storefront_count: number;
  storefront_included: number;
  storefront_extra_count: number;
  storefront_extra_cost_thb: number;
  apps: TenantUsageApp[];
  active_app_count: number;
  app_total_cost_thb: number;
  estimated_monthly_total_thb: number;
  plan: TenantUsagePlan;
  resources?: TenantUsageResource[];
}

const EMPTY_PLAN: TenantUsagePlan = {
  price_per_user_thb: 450,
  storage_included_gb: 10,
  storage_overage_per_gb_thb: 5,
  storefronts_included: 1,
  storefront_extra_per_month_thb: 199,
  items_limit: 0,
  orders_per_month_limit: 0,
  marketplace_connections_limit: 0,
  customers_limit: 0,
  ai_queries_per_day_limit: 0,
  builder_pages_limit: 0,
  employees_limit: 0,
};

const EMPTY_RESPONSE: TenantUsageResponse = {
  plan_name: null,
  plan_status: null,
  user_count: 0,
  user_cost_thb: 0,
  storage_bytes: 0,
  storage_gb: 0,
  storage_included_gb: 10,
  storage_overage_gb: 0,
  storage_overage_cost_thb: 0,
  storage_metered_at: null,
  storefront_count: 0,
  storefront_included: 1,
  storefront_extra_count: 0,
  storefront_extra_cost_thb: 0,
  apps: [],
  active_app_count: 0,
  app_total_cost_thb: 0,
  estimated_monthly_total_thb: 0,
  plan: EMPTY_PLAN,
  resources: [],
};

// ── Fetcher ──────────────────────────────────────────────

async function fetchTenantUsage(): Promise<TenantUsageResponse> {
  try {
    const token = getCPToken();
    if (!token) return EMPTY_RESPONSE;

    const response = await fetch(
      `${CONTROL_PLANE_URL}/api/method/control_plane.api.tenant_usage.get_tenant_usage`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "omit",
        body: JSON.stringify({ access_token: token }),
      }
    );

    if (!response.ok) return EMPTY_RESPONSE;

    const data = await response.json();
    return (data.message as TenantUsageResponse) ?? EMPTY_RESPONSE;
  } catch {
    return EMPTY_RESPONSE;
  }
}

// ── Hook ─────────────────────────────────────────────────

export function useGetTenantUsage(): UseQueryResult<TenantUsageResponse, Error> & {
  isFetched: boolean;
} {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const hasHydrated = useAuthStore((s) => s.hasHydrated);
  const currentTenant = useAuthStore((s) => s.currentTenant);

  const result = useQuery({
    queryKey: ["tenant-usage", currentTenant],
    queryFn: fetchTenantUsage,
    staleTime: 5 * 60 * 1000, // 5 minutes — usage data changes at most hourly
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: true,
    retry: 1,
    enabled: hasHydrated && isAuthenticated && !!currentTenant,
  });

  return { ...result, isFetched: result.isFetched };
}
